@use '@fukuri-admins/ui/layout' as *;

:root {
  --border: hsl(214.3 31.8% 91.4%);
  --input: hsl(214.3 31.8% 91.4%);
  --ring: hsl(222.2 84% 4.9%);
  --background: hsl(0 0% 100%);
  --foreground: hsl(222.2 84% 4.9%);
  --muted: hsl(210 40% 96%);
  --muted-foreground: hsl(215.4 16.3% 46.9%);
  --popover: hsl(0 0% 100%);
  --popover-foreground: hsl(222.2 84% 4.9%);
  --accent: hsl(210 40% 96%);
  --accent-foreground: hsl(222.2 84% 4.9%);
  --radius: 8px;
  --ring-offset-width: 2px;
  --ring-offset-color: hsl(0 0% 100%);
}

.trigger {
  display: flex;
  height: 40px; // h-10
  width: 100%;
  align-items: center;
  justify-content: space-between;
  border-radius: calc(var(--radius) - 2px);
  border: 1px solid border('Quaternary');
  background-color: var(--background);
  padding: 8px 12px; // px-3 py-2
  font-size: 14px; // text-sm
  line-height: 20px;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px border('Focus');
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  
  &[data-placeholder] {
    color: var(--muted-foreground);
  }
  
  // line-clamp-1 equivalent
  & > span {
    overflow: hidden;
    display: block;
  }
}

.triggerIcons {
  display: flex;
  align-items: center;
  gap: spacing('2');
}

.clearButton {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--muted-foreground);

  &:hover {
    color: var(--foreground);
    opacity: 0.5;
  }
}

.triggerIcon {
  opacity: 0.5;
}

.scrollButton {
  display: flex;
  cursor: default;
  align-items: center;
  justify-content: center;
  padding: 4px 0; // py-1
}

.scrollIcon {
  height: 16px; // h-4
  width: 16px; // w-4
}

.content {
  position: relative;
  z-index: 99999;
  max-height: var(--radix-select-content-available-height);
  min-width: 128px; // 8rem
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: calc(var(--radius) - 2px);
  border: 1px solid border('Quaternary');
  background-color: var(--popover);
  color: var(--popover-foreground);
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  
  // アニメーション
  &[data-state="open"] {
    animation: fadeIn 0.15s ease-out, zoomIn 0.15s ease-out;
  }
  
  &[data-state="closed"] {
    animation: fadeOut 0.15s ease-in, zoomOut 0.15s ease-in;
  }
  
  // スライドアニメーション
  &[data-side="bottom"] {
    animation: fadeIn 0.15s ease-out, zoomIn 0.15s ease-out, slideInFromTop 0.15s ease-out;
  }
  
  &[data-side="left"] {
    animation: fadeIn 0.15s ease-out, zoomIn 0.15s ease-out, slideInFromRight 0.15s ease-out;
  }
  
  &[data-side="right"] {
    animation: fadeIn 0.15s ease-out, zoomIn 0.15s ease-out, slideInFromLeft 0.15s ease-out;
  }
  
  &[data-side="top"] {
    animation: fadeIn 0.15s ease-out, zoomIn 0.15s ease-out, slideInFromBottom 0.15s ease-out;
  }
  
  transform-origin: var(--radix-select-content-transform-origin);
}

.contentPopper {
  &[data-side="bottom"] {
    transform: translateY(4px);
  }
  
  &[data-side="left"] {
    transform: translateX(-4px);
  }
  
  &[data-side="right"] {
    transform: translateX(4px);
  }
  
  &[data-side="top"] {
    transform: translateY(-4px);
  }
}

.viewport {
  padding: 4px; // p-1
}

.viewportPopper {
  height: var(--radix-select-trigger-height);
  width: 100%;
  min-width: var(--radix-select-trigger-width);
}

.label {
  padding: 6px 8px 6px 32px; // py-1.5 pl-8 pr-2
  font-size: 14px; // text-sm
  line-height: 20px;
  font-weight: 600; // font-semibold
}

.item {
  position: relative;
  display: flex;
  width: 100%;
  cursor: default;
  user-select: none;
  align-items: center;
  border-radius: calc(var(--radius) - 4px);
  padding: 6px 8px 6px 32px; // py-1.5 pl-8 pr-2
  font-size: 14px; // text-sm
  line-height: 20px;
  outline: none;
  
  &:focus {
    background-color: var(--accent);
    color: var(--accent-foreground);
  }
  
  &[data-disabled] {
    pointer-events: none;
    opacity: 0.5;
  }
}

.itemIndicator {
  position: absolute;
  left: 8px; // left-2
  display: flex;
  height: 14px; // h-3.5
  width: 14px; // w-3.5
  align-items: center;
  justify-content: center;
}

.checkIcon {
  height: 16px; // h-4
  width: 16px; // w-4
}

.separator {
  margin: 4px -4px; // my-1 -mx-1
  height: 1px;
  background-color: var(--muted);
}

// アニメーションキーフレーム
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes zoomIn {
  from {
    transform: scale(0.95);
  }
  to {
    transform: scale(1);
  }
}

@keyframes zoomOut {
  from {
    transform: scale(1);
  }
  to {
    transform: scale(0.95);
  }
}

@keyframes slideInFromTop {
  from {
    transform: translateY(-8px);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes slideInFromRight {
  from {
    transform: translateX(8px);
  }
  to {
    transform: translateX(0);
  }
}

@keyframes slideInFromLeft {
  from {
    transform: translateX(-8px);
  }
  to {
    transform: translateX(0);
  }
}

@keyframes slideInFromBottom {
  from {
    transform: translateY(8px);
  }
  to {
    transform: translateY(0);
  }
}
